import { Spinner } from "@/components/ui/spinner";
import { UnreadDot } from "@/components/ui/unread-dot";
import { cn } from "@/lib/utils";
import type { AssistantConversationStatus } from "../../types";

/**
 * The status half of the conversation vocabulary — two marks and nothing else.
 *
 * `working` is an accent spinner, `needs_you` is the blue `UnreadDot`, `idle` draws nothing.
 * The two marks never appear together and neither carries the row's *state* (current / hover),
 * which is a separate axis owned by `ConversationRow`.
 *
 * Placement differs by mark and is the caller's job: the spinner sits in the row's left
 * gutter (it reads as "this one is running"), the dot sits at the right beside the time (it
 * reads as "this one is waiting for you"), exactly as in the design.
 */
export function ConversationStatusMark({
  status,
  size = "default",
  className,
}: {
  status: AssistantConversationStatus;
  /** `default` matches the 13/11 flyout scale, `dense` the panel's 12/11. */
  size?: "default" | "dense";
  className?: string;
}) {
  if (status === "working") {
    return (
      <Spinner
        className={cn(
          "text-primary",
          size === "dense" ? "size-3" : "size-3.5",
          "shrink-0",
          className
        )}
        // Decorative: the row's own `aria-label` already says "Working", so the spinner's
        // default `role="status"` would announce the same fact a second time.
        aria-hidden="true"
      />
    );
  }

  if (status === "needs_you") {
    // The dot keeps its 7px at both densities. It is a mark in a fixed vocabulary, not a piece
    // of type — shrinking it with the text would make "needs you" quieter in the panel than in
    // the flyout, which is the one thing this signal must never be.
    return <UnreadDot active className={className} />;
  }

  return null;
}
